import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Snackbar,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import { useAuth } from 'react-oidc-context';
import CreateApi from '../../../shared/Settings/appSettings';
import { useTBContext } from '../../Contexts/useTBStates';
import type { CourseTeeSetData, EventScoreWithId, TbRegistration, ViewEventData, scoreHole } from '@moment-golf/types';
import { ScorecardTemplateDesigner } from './ScorecardTemplateDesigner';
import { ScorecardCardItem } from './ScorecardCardItem';
import { useScorecardPDF } from './useScoreCardPDF';
import type { ScorecardPDFLayout } from './useScoreCardPDF';
import {
    DEFAULT_SCORECARD_TEMPLATE,
    buildScoringUnits,
} from './scorecardTemplateTypes';
import type { ScorecardTemplate, ScoringUnit } from './scorecardTemplateTypes';

// ─── Theme palette — mirrors TeeSheetDisplay / ScoreCodeCards ─────────────────

const PRIMARY = {
    main: 'rgba(30, 78, 55, 1)',
} as const;

const SEPIA = {
    bg: 'rgb(253, 252, 248)',
    light: 'rgb(189, 148, 98)',
} as const;

// ─── Props ────────────────────────────────────────────────────────────────────

interface ScorecardCardsProps {
    registrations: TbRegistration[];
    roundValue: number;
    viewEvent: Record<string, unknown> | null;
    viewEventData: ViewEventData;
    factor?: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ScorecardCards: React.FC<ScorecardCardsProps> = ({
    registrations,
    roundValue,
    viewEvent,
    viewEventData,
    factor = 1,
}) => {
    const auth = useAuth();
    const api  = CreateApi();
    const { setViewEvent, setEventData, eventData } = useTBContext();

    const [template, setTemplate]           = useState<ScorecardTemplate>(DEFAULT_SCORECARD_TEMPLATE);
    const [designerOpen, setDesignerOpen]   = useState(false);
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const [saving, setSaving]               = useState(false);
    const [snack, setSnack]                 = useState<string | null>(null);
    const [selectedUnit, setSelectedUnit]   = useState<ScoringUnit | null>(null);
    const [showScores, setShowScores]       = useState(false);
    const [blankMode, setBlankMode]         = useState(false);
    const [layout, setLayout]               = useState<ScorecardPDFLayout>('2up');

    const { generatePDF } = useScorecardPDF();

    // ── Hydrate template whenever a new event is selected ─────────────────────
    useEffect(() => {
        const saved = (viewEvent as any)?.scorecardTemplate;
        setTemplate(saved ?? DEFAULT_SCORECARD_TEMPLATE);
    }, [(viewEvent as any)?._id]);

    // ── Resolve primary course data ────────────────────────────────────────────
    const courseData = useMemo(
        () => ((viewEventData.courseData as CourseTeeSetData[])[0]) ?? null,
        [viewEventData.courseData],
    );

    // ── Build scoring units (filtered, merged, sorted by tee time) ─────────────
    const scoringUnits = useMemo(
        () => buildScoringUnits(registrations, roundValue),
        [registrations, roundValue],
    );

    // Default selection to the first unit whenever the sorted list changes
    useEffect(() => {
        setSelectedUnit((prev) => {
            if (prev) {
                const still = scoringUnits.find((u) => u.scoreCode === prev.scoreCode);
                if (still) return still;
            }
            return scoringUnits[0] ?? null;
        });
    }, [scoringUnits]);

    // ── Resolve EventScoreWithId for the selected unit ─────────────────────────
    const selectedScores = useMemo<EventScoreWithId | null>(() => {
        if (!showScores || !selectedUnit?.eventScoreID) return null;
        return (viewEventData.scores as EventScoreWithId[]).find(
            (s) => String((s as any)._id) === String(selectedUnit.eventScoreID),
        ) ?? null;
    }, [showScores, selectedUnit, viewEventData.scores]);

    // ── Resolve marker scores for the preview card ─────────────────────────────
    const { selectedMarkerScores, selectedMarkerLabel, selectedMarkerScoreCode } = useMemo<{
        selectedMarkerScores: scoreHole[] | null;
        selectedMarkerLabel: string;
        selectedMarkerScoreCode: string;
    }>(() => {
        // ── Full diagnostic dump ───────────────────────────────────────────────
        console.group('[ScorecardCards] preview marker diagnostic');
        console.log('showScores:', showScores, '| roundValue:', roundValue);
        console.log('selectedScores (full):', JSON.parse(JSON.stringify(selectedScores ?? null)));
        console.log('viewEventData.scores (full):', JSON.parse(JSON.stringify((viewEventData.scores as EventScoreWithId[]) ?? [])));
        console.groupEnd();

        if (!showScores || !selectedScores) {
            return { selectedMarkerScores: null, selectedMarkerLabel: '', selectedMarkerScoreCode: '' };
        }
        const roundScore = selectedScores.roundScores?.find(
            (rs) => rs.roundNumber === roundValue,
        );
        const markerScoreCode = (roundScore as any)?.markerScoreCode ?? null;
        console.log('[ScorecardCards] roundScore for round', roundValue, '(full):', JSON.parse(JSON.stringify(roundScore ?? null)));
        console.log('[ScorecardCards] markerScoreCode:', markerScoreCode);
        if (!markerScoreCode) {
            return { selectedMarkerScores: null, selectedMarkerLabel: '', selectedMarkerScoreCode: '' };
        }
        const markerEventScore = (viewEventData.scores as EventScoreWithId[]).find((s) =>
            s.roundScores?.some(
                (rs) => rs.roundNumber === roundValue && rs.playerScoreCode === markerScoreCode,
            ),
        );
        const holeScores = markerEventScore?.roundScores.find(
            (rs) => rs.roundNumber === roundValue,
        )?.holeScores ?? [];

        const markerNames: string[] = [];
        for (const unit of scoringUnits) {
            for (const reg of unit.registrations) {
                if (reg.scoreCode === markerScoreCode) {
                    markerNames.push(reg.playerName ?? '');
                }
            }
        }
        const markerLabel = markerNames.filter(Boolean).join(' / ');

        console.log('[ScorecardCards] resolved marker →', { markerScoreCode, markerLabel, holeScoreCount: holeScores.length });

        return {
            selectedMarkerScores: holeScores.length > 0 ? holeScores : null,
            selectedMarkerLabel: markerLabel,
            selectedMarkerScoreCode: markerScoreCode,
        };
    }, [showScores, selectedScores, viewEventData.scores, scoringUnits, roundValue]);

    const eventDataRecord = (viewEvent ?? {}) as Record<string, unknown>;

    // ── Upload event logo image (forwarded from designer) ──────────────────────
    const handleImageUpload = async (file: File): Promise<string> => {
        const eventId = (viewEvent as any)?._id;
        if (!eventId) throw new Error('No event selected');

        const formData = new FormData();
        formData.append('image', file);
        formData.append('_id', eventId);
        formData.append('userID', auth.user?.profile.sub ?? '');

        const response = await api.post('/image/uploadEventLogo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (response.status !== 200) throw new Error('Upload failed');
        return response.data.imageUrl as string;
    };

    // ── Save template to event document ───────────────────────────────────────
    const handleSaveTemplate = async (savedTemplate: ScorecardTemplate) => {
        setTemplate(savedTemplate);
        setDesignerOpen(false);

        const eventId = (viewEvent as any)?._id;
        if (!eventId) return;

        setSaving(true);
        try {
            await api.post('/tb/UpdateEvent', {
                _id: eventId,
                userID: auth.user?.profile.sub,
                scorecardTemplate: savedTemplate,
            });

            const updatedEvent = { ...(viewEvent as any), scorecardTemplate: savedTemplate };
            setViewEvent(updatedEvent);
            setEventData(
                (eventData as any[]).map((e: any) =>
                    e._id === eventId ? { ...e, scorecardTemplate: savedTemplate } : e,
                ),
            );

            setSnack('Template saved to event.');
        } catch (err) {
            console.error('Error saving scorecard template:', err);
            setSnack('Failed to save template — changes kept locally.');
        } finally {
            setSaving(false);
        }
    };

    // ── PDF generation ─────────────────────────────────────────────────────────
    const handlePrint = async (
        units: ScoringUnit[],
        opts?: { template?: ScorecardTemplate; showScores?: boolean; blankMode?: boolean; layout?: ScorecardPDFLayout },
    ) => {
        if (units.length === 0) return;
        setPdfGenerating(true);
        try {
            await generatePDF({
                units,
                template: opts?.template ?? template,
                layout: opts?.layout ?? layout,
                eventData: eventDataRecord,
                courseData,
                scores: (opts?.showScores ?? showScores) ? viewEventData.scores as EventScoreWithId[] : [],
                roundValue,
                blankMode: opts?.blankMode ?? blankMode,
            });
        } finally {
            setPdfGenerating(false);
        }
    };

    const handlePrintTest = async (opts: {
        template: ScorecardTemplate;
        scoringUnit: ScoringUnit;
        showScores: boolean;
        blankMode: boolean;
    }) => {
        setPdfGenerating(true);
        try {
            await generatePDF({
                units: [opts.scoringUnit],
                template: opts.template,
                layout: '1up',
                eventData: eventDataRecord,
                courseData,
                scores: opts.showScores ? viewEventData.scores as EventScoreWithId[] : [],
                roundValue,
                blankMode: opts.blankMode,
            });
        } finally {
            setPdfGenerating(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────

    return (
        <Stack
            direction="column"
            sx={{
                width: '100%',
                maxWidth: 1200,
                flex: 1,
                alignSelf: 'stretch',
                minHeight: 0,
                border: `1.5px solid ${SEPIA.light}`,
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: SEPIA.bg,
                boxShadow: '0 2px 10px rgba(0,0,0,0.10)',
                boxSizing: 'border-box',
            }}
        >
            {/* ── Header bar ────────────────────────────────────────────────── */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                sx={{
                    flexShrink: 0,
                    backgroundColor: SEPIA.bg,
                    px: 2,
                    py: 1.25,
                    gap: 1,
                    borderBottom: `1px solid ${SEPIA.light}`,
                }}
            >
                <Typography
                    sx={{
                        fontSize: 16 * factor,
                        fontWeight: 700,
                        color: PRIMARY.main,
                        letterSpacing: '0.04em',
                        flexShrink: 0,
                    }}
                >
                    Scorecards
                </Typography>

                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                    {/* Layout toggle */}
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={layout}
                        onChange={(_, val: ScorecardPDFLayout | null) => { if (val) setLayout(val); }}
                        sx={{ mr: 0.5 }}
                    >
                        <ToggleButton
                            value="1up"
                            sx={{ fontSize: 11 * factor, textTransform: 'none', px: 1.25, py: 0.5 }}
                        >
                            1-up
                        </ToggleButton>
                        <ToggleButton
                            value="2up"
                            sx={{ fontSize: 11 * factor, textTransform: 'none', px: 1.25, py: 0.5 }}
                        >
                            2-up
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* Design Template */}
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PaletteOutlinedIcon sx={{ fontSize: `${14 * factor}px !important` }} />}
                        onClick={() => setDesignerOpen(true)}
                        sx={{
                            fontSize: 12 * factor,
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1.5,
                        }}
                    >
                        Design Template
                    </Button>

                    {/* Print Selected */}
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                            pdfGenerating
                                ? <CircularProgress size={14} />
                                : <DownloadIcon sx={{ fontSize: `${14 * factor}px !important` }} />
                        }
                        disabled={pdfGenerating || !selectedUnit}
                        onClick={() => { if (selectedUnit) handlePrint([selectedUnit]); }}
                        sx={{
                            fontSize: 12 * factor,
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1.5,
                        }}
                    >
                        Print Selected
                    </Button>

                    {/* Print All */}
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={
                            pdfGenerating
                                ? <CircularProgress size={14} />
                                : <DownloadIcon sx={{ fontSize: `${14 * factor}px !important` }} />
                        }
                        disabled={pdfGenerating || scoringUnits.length === 0}
                        onClick={() => handlePrint(scoringUnits)}
                        sx={{
                            fontSize: 12 * factor,
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1.5,
                        }}
                    >
                        {pdfGenerating ? 'Generating…' : 'Print All'}
                    </Button>

                    {saving && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <CircularProgress size={12} />
                            <Typography variant="caption" color="text.secondary">Saving…</Typography>
                        </Stack>
                    )}
                </Stack>
            </Stack>

            {/* ── Summary strip ─────────────────────────────────────────────── */}
            <Stack
                direction="row"
                alignItems="center"
                sx={{
                    flexShrink: 0,
                    px: 2,
                    py: 0.75,
                    borderBottom: `1px solid ${SEPIA.light}`,
                    backgroundColor: SEPIA.bg,
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    {scoringUnits.length} scoring unit{scoringUnits.length !== 1 ? 's' : ''}
                </Typography>
            </Stack>

            {/* ── Preview area ───────────────────────────────────────────────── */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    px: 2,
                    py: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                {scoringUnits.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 180,
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            No active registrations for this event.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Controls row */}
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <Autocomplete
                                size="small"
                                options={scoringUnits}
                                value={selectedUnit}
                                onChange={(_, val) => setSelectedUnit(val)}
                                getOptionLabel={(unit) =>
                                    `${unit.playerName}${unit.teeTime ? ` · ${unit.teeTime}` : ''}`
                                }
                                isOptionEqualToValue={(a, b) => a.scoreCode === b.scoreCode}
                                renderInput={(params) => (
                                    <TextField {...params} label="Preview Player / Unit" />
                                )}
                                sx={{ flex: 1, maxWidth: 440 }}
                            />
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: 12 * factor }}
                                >
                                    Show Scores
                                </Typography>
                                <Switch
                                    size="small"
                                    checked={showScores}
                                    onChange={(e) => setShowScores(e.target.checked)}
                                />
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: 12 * factor }}
                                >
                                    Print Blank
                                </Typography>
                                <Switch
                                    size="small"
                                    checked={blankMode}
                                    onChange={(e) => setBlankMode(e.target.checked)}
                                />
                            </Stack>
                        </Stack>

                        {/* Single-card preview */}
                        {selectedUnit && (
                            <Box
                                sx={{
                                    overflowX: 'auto',
                                    border: `1px solid ${SEPIA.light}`,
                                    borderRadius: 1,
                                    backgroundColor: '#fff',
                                    p: 1,
                                    alignSelf: 'flex-start',
                                    maxWidth: '100%',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <ScorecardCardItem
                                    template={template}
                                    scoringUnit={selectedUnit}
                                    eventData={eventDataRecord}
                                    courseData={courseData}
                                    scores={selectedScores}
                                    markerScores={selectedMarkerScores}
                                    markerLabel={selectedMarkerLabel}
                                    markerScoreCode={selectedMarkerScoreCode}
                                    roundValue={roundValue}
                                    blankMode={blankMode}
                                    showBorder={false}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* ── Template designer dialog ───────────────────────────────────── */}
            <ScorecardTemplateDesigner
                open={designerOpen}
                template={template}
                previewScoringUnit={selectedUnit ?? scoringUnits[0] ?? null}
                allScoringUnits={scoringUnits}
                previewEventData={eventDataRecord}
                previewCourseData={courseData}
                previewScores={viewEventData.scores as EventScoreWithId[]}
                roundValue={roundValue}
                onImageUpload={handleImageUpload}
                onSave={handleSaveTemplate}
                onPrintTest={handlePrintTest}
                onClose={() => setDesignerOpen(false)}
            />

            {/* ── Save feedback ─────────────────────────────────────────────── */}
            <Snackbar
                open={snack !== null}
                autoHideDuration={3500}
                onClose={() => setSnack(null)}
                message={snack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Stack>
    );
};
